import type { GridRowParams, GridSortModel } from "@mui/x-data-grid-premium";

import { Stack } from "@mui/material";
import { ManifestInboundEmailQuery, ReceivedEmailStatus } from "generated-graphql/graphql";
import { OmnisearchDataGridV2 } from "hooks/omnisearch-v2";
import { useCallback, useMemo } from "react";

import type { InboundEmailRow } from "./senderDialogValues";

import { InboundEmailDetail } from "./InboundEmailDetail";
import {
  buildInboundColumns,
  INBOUND_ADDITIONAL_FILTER_KEYS,
  INBOUND_EXCLUDE_FILTER_COLUMNS,
} from "./inboundGridColumns";
import { INBOUND_EMAIL_QUERY } from "./inboundQuery";
import { SectionHeading } from "./SectionHeading";

const INITIAL_SORT: GridSortModel = [{ field: "receivedAt", sort: "desc" }];

/**
 * Deliberately small. Two grids stack on this page, so a tall default makes the
 * second one a scroll away; anyone who wants more can raise it. It also decides
 * whether the footer appears at all — the shared DataGrid hides it, and with it the
 * row count, whenever the rows fit inside the smallest page size.
 */
const INITIAL_PAGE_SIZE = 5;
const PAGE_SIZE_OPTIONS = [5, 10, 25, 50, 100];

/**
 * The V2 grid floors its scroller at 300px for "consistent layout", which suits its
 * own default of 25 rows. At five that leaves a band of blank white above the footer.
 * Let the rows size it, and keep a smaller floor for the empty-state message so a
 * grid with nothing in it is not a tall white box.
 */
const GRID_SX = {
  "& .MuiDataGrid-virtualScroller": { minHeight: 0 },
  "& .MuiDataGrid-overlayWrapper, & .MuiDataGrid-overlayWrapperInner": { minHeight: 120 },
};

const getItems = (data: ManifestInboundEmailQuery) => data.receivedEmails.items;
const getCount = (data: ManifestInboundEmailQuery) => data.receivedEmails.count;

const renderDetailPanel = ({ row }: GridRowParams<InboundEmailRow>) => (
  <InboundEmailDetail id={row.id} />
);

/** Fixed, with the panel scrolling internally: a long body must not push the grid away. */
const detailPanelHeight = () => 360;

function toSortInput(model: GridSortModel) {
  return model
    .filter((entry) => entry.sort)
    .map((entry) => ({ field: entry.field, sort: entry.sort === "asc" ? "ASC" : "DESC" }));
}

/**
 * Every inbound manifest email, across every organization.
 *
 * One grid rather than the two it replaces. The old split was on whether resolution
 * produced a tenant, which is an implementation outcome rather than a question anyone
 * asks — and it put the same rejection reason on either side of the page, since a
 * disabled sender is attributable only when exactly one organization held the entry.
 * "Unattributed" is a filter value here.
 *
 * Omnisearch is URL-driven, so a view can be linked to. That is what the old page
 * could not do: three grids shared it and omnisearch has no URL namespacing, so all
 * three would have read the same term.
 *
 * The default view — the rows worth acting on — is seeded by the Observe tab's own
 * link (`MANIFEST_EMAIL_TAB`) rather than by this component. `useOmnisearchState`'s
 * `initialFilters` only applies in local mode, and a mount effect that rewrote the URL
 * would fight anyone arriving at a link of their own.
 */
export function InboundEmailGrid({
  onAddSender,
  onReplay,
  onRoute,
  onDismiss,
  busy,
}: {
  onAddSender: (fromAddress: string) => void;
  onReplay: (row: InboundEmailRow) => void;
  onRoute: (row: InboundEmailRow) => void;
  onDismiss: (row: InboundEmailRow, dismissed: boolean) => void;
  /** The row a re-run was just asked for, so it stops offering the same action twice. */
  busy?: { id: string; status: ReceivedEmailStatus } | null;
}) {
  const columns = useMemo(
    () => buildInboundColumns({ onAddSender, onReplay, onRoute, onDismiss, busy }),
    [onAddSender, onReplay, onRoute, onDismiss, busy]
  );

  const buildVariables = useCallback(
    (search: string, pagination: { page: number; pageSize: number }, sort: GridSortModel) => ({
      search,
      page: pagination.page,
      pageSize: pagination.pageSize,
      sort: toSortInput(sort),
    }),
    []
  );

  return (
    <Stack spacing={1.5}>
      <SectionHeading
        title="Inbound Mail"
        description="Every message sent to the intake address, across all organizations."
      />

      <OmnisearchDataGridV2
        // Bumped when the Outcome column became Status and Attention was added: saved
        // column state is keyed on field names, so the old key would restore a layout
        // that half-matches.
        persistenceKey="manifest-email-inbound-v2"
        query={INBOUND_EMAIL_QUERY}
        getItems={getItems}
        getCount={getCount}
        buildVariables={buildVariables}
        columns={columns}
        additionalFilterKeys={INBOUND_ADDITIONAL_FILTER_KEYS}
        excludeFilterColumns={INBOUND_EXCLUDE_FILTER_COLUMNS}
        initialSortModel={INITIAL_SORT}
        initialPageSize={INITIAL_PAGE_SIZE}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        noRowsMessage="No inbound email matches this view."
        getDetailPanelContent={renderDetailPanel}
        getDetailPanelHeight={detailPanelHeight}
        disableRowSelectionOnClick
        compact
        gridSx={GRID_SX}
      />
    </Stack>
  );
}
